export const distributionPlannerEn = {
  pageTitle: "Distribution Planner",
  pageSubtitle:
    "Review how many stores range each SKU, set min/max store targets and re-balance distribution across the fleet",

  category: {
    title: "Category",
    placeholder: "category",
    prompt: "Select a category to start planning distribution",
    loading: "Loading categories...",
    loadFailed: "Unable to load categories.",
    invalid: "Invalid category.",
  },

  status: {
    loading: "Loading distribution...",
    loadError: "Unable to load the distribution plan.",
  },

  actions: {
    saveTargets: "Save targets",
    runDistribution: "Run distribution",
  },

  readOnly: {
    hint: "Read-only view: you can filter and export this plan. Editing targets and running the distribution need the inputs-update permission.",
  },

  categoriesEntry: {
    button: "Distribution",
  },

  priority: {
    label: "Run priority:",
    core: "Core range",
    minMax: "Min/max store targets",
    spaceMix: "Space mix targets",
    hint: "Earlier steps win when constraints conflict: core range is placed first and never removed, min/max store counts bind next, and space-mix targets steer only the shelf left over.",
  },

  tiles: {
    storeFleet: "Store fleet",
    storeFleetHint: "Stores in this category",
    masterList: "Master list",
    masterListHint: "Products in scope",
    withinTarget: "Within target",
    belowMin: "Below min",
    aboveMax: "Above max",
    noTarget: "No target set",
  },

  spaceMix: {
    title: "Space mix",
    subtitle:
      "Share of category sales by segment today, against the mix you would like to plan towards. Targets are saved with the category and override the next distribution run; the selector below decides whether a target is one fleet-wide total or the same share in every store. Segments without a target keep following each store's own sales mix.",
    mode: {
      label: "How targets apply",
      network: "Network total, tilted per store",
      uniform: "Uniform share in every store",
      explainer:
        "Network total: the target is a fleet-wide share of shelf. The run tilts each store's own sales mix up or down until the total across all stores lands on the target, so store-to-store demand differences survive. Uniform: every store's shelf is planned with exactly the target share — one identical mix across the whole fleet, at the cost of local demand differences. Per-cluster targets are not supported.",
    },
    segment: "Segment",
    skus: "{{count}} SKUs",
    currentShare: "Current share",
    currentShareExplainer:
      "A segment's current share is its sales-based return on space: how much of the category's sales the segment's SKUs bring in.",
    equationLhs: "Current share (%)",
    equationNumerator: "Σ sales of the segment's SKUs across all stores",
    equationDenominator: "Σ sales of every SKU in the category across all stores",
    equationNote:
      "Sales are summed across every store, whether or not a SKU is currently ranged there — the share measures demand and does not move when distribution changes.",
    targetShare: "Target %",
    targetShareExplainerUniform:
      "Targets apply uniformly: every store is planned to the same segment share, replacing that store's own sales mix. A store that over- or under-indexes on the segment gets the same target, so treat it as a deliberate chain-wide override rather than a demand forecast. Per-SKU min/max store limits still bind, so realized shares will differ store by store.",
    targetShareExplainerNetwork:
      "Targets apply as a network total: the run tilts each store's own sales mix by a common factor until the capacity-weighted total across all stores hits the target. Stores that over-index on the segment stay above it and under-indexing stores stay below — only the fleet total lands on the number. Per-SKU min/max store limits still bind.",
    delta: "Delta",
    onTarget: "On target",
    points: "{{value}} pt",
    totalAllocated: "Total allocated",
    ofHundred: "of 100%",
    reset: "Reset to current mix",
    reservedNote:
      "Min-store targets, core-range SKUs and mandatory per-store coverage reserve ~{{reserved}}% of total shelf — space-mix targets can only steer the remaining ~{{free}}%.",
    reservedExplainer:
      "A min-store target is a store count, but each of those stores must give the SKU physical space: its shelf width × a system-defined average facing. Core-range SKUs count the same way — each one is a guaranteed placement in its store, ranged before anything else. Every store must also carry at least one SKU of every segment, so per store the narrowest eligible SKU of each segment is reserved too — or the core SKUs already placed there. A segment's floor is the larger of its minimums-and-core and that coverage; summing the floors, divided by the fleet's total shelf capacity, gives the reserved share. It is a fleet-wide aggregate — individual stores can be committed more or less than this.",
    unreachableTarget:
      "Unreachable target: max-store limits and the shelf other segments' minimums, core range and coverage reserve cap {{segment}} at about {{max}}% of shelf.",
    forcedLowTarget:
      "Already above target: {{segment}}'s own minimums, core range and per-store coverage reserve about {{min}}% of shelf, so the run cannot go lower.",
    noData: "No segments to show for this category.",
    unassigned: "Unassigned",
    automaticTarget:
      "Automatic: no target set. This segment takes its share of whatever the explicit targets leave, following current sales.",
    automaticLabel: "Auto",
    feasibilityStale:
      "Feasibility reflects the last saved min/max targets — {{count}} unsaved product edit(s) are not included until you save.",
    allocation: {
      automatic: "No explicit targets: 100% is automatic, following each store's demand.",
      over: "Explicit targets sum to {{total}}% — above 100%. The run scales them all down proportionally (their relative sizes survive), and segments without a target get no share of their own.",
      underComplete:
        "Every segment has an explicit target but they sum to {{total}}% — below 100%. The leftover has nowhere to flow, so the run scales the targets up proportionally to fill the shelf.",
      underPartial:
        "Explicit targets sum to {{total}}%. The remaining {{rest}}% flows to the {{count}} segment(s) without a target, following each store's own sales mix.",
    },
    clusters: {
      networkTab: "All clusters",
      networkHint: "network total",
      storeCount: "{{count}} stores",
      unclustered: "No cluster",
      targetShare: "Target %",
      clusterShare: "Cluster share",
      simInput: "Simulate sales (± pt)",
      adjustedShare: "Adjusted share",
      networkNote:
        "While a simulation is active the target is the sales-weighted roll-up of your per-cluster simulated mix; otherwise it shows the saved targets, or today's share where none is saved. Open a cluster tab to simulate updated segment sales; saving persists the roll-up as the targets.",
      clusterNote:
        "Pick + or − and enter percentage points to simulate this segment's updated sales in this cluster. The adjusted segments hold their new share and the rest of the cluster rebalances to 100%. Saving persists the resulting network target.",
    },
  },

  scatter: {
    title: "Distribution vs sales per store",
    subtitle:
      "Each point is a SKU in the current filter. Reference lines mark the median of the visible SKUs.",
    xAxis: "Stores ranged",
    yAxis: "Avg sales / store",
    hintExpand: "high sales/store, low distribution — expand?",
    hintCut: "low sales/store, high distribution — cut?",
    tooltipDistribution: "Distribution",
    tooltipAvgSales: "Avg sales / store",
    tooltipStores: "{{count}} stores",
    noData: "No SKUs match the current filters.",
    legendNoTarget: "No target",
    legendOk: "Within target",
    legendBelow: "Below min",
    legendAbove: "Above max",
  },

  table: {
    title: "Products",
    searchPlaceholder: "Search name, brand or product code",
    allOf: "All {{name}}",
    coreAll: "All SKUs",
    coreOnly: "Core range only",
    coreNon: "Non-core only",
    statusAll: "All statuses",
    statusNone: "No target set",
    statusOk: "Within target",
    statusBelow: "Below min",
    statusAbove: "Above max",
    results: "{{count}} of {{total}} SKUs",
    dirtyHint: "{{count}} unsaved min/max edits",
    loading: "Loading products...",
    noRows: "No products match the current filters.",
    yes: "Yes",
    none: "—",
    exportCsv: "Export CSV",
    importCsv: "Import CSV",
    importCsvHint: "Import min/max targets by product code",
  },

  columns: {
    product: "Product",
    core: "Core",
    avgSales: "Avg sales/store",
    distribution: "Distribution",
    min: "Min",
    max: "Max",
    status: "Status",
  },

  statusLabels: {
    ok: "Within target",
    below: "Below min",
    above: "Above max",
    none: "No target",
  },

  bulk: {
    hint: "Tick the checkboxes in the first column (or the header checkbox) to set min/max store targets for many SKUs at once.",
    selected: "{{count}} SKUs selected",
    min: "Set min",
    max: "Set max",
    setMin: "Set min",
    setMax: "Set max",
    clearMinMax: "Clear min/max",
    clearSelection: "Clear selection",
  },

  run: {
    title: "Run distribution",
    subtitle: "Recompute store assortments for this category",
    explanation:
      "Recomputes every SKU's store assortment for this category. Core-range SKUs are placed first and never removed; then each SKU's min/max store targets, fixture capacity, variant coverage and your saved space-mix targets steer the rest — targets applied per your saved mode: uniformly in every store, or as a network total tilted per store. This rewrites live store assortments.",
    skus: "SKUs",
    storeFleet: "Store fleet",
    withTargets: "With min/max set",
    warningTitle: "This changes live store assortments",
    warningBody:
      "Store counts are decided by the run, not entered here. Every SKU in the category is reallocated, so distribution can go up or down for any of them.",
    savesDirtyTargets:
      "{{count}} unsaved min/max edits will be saved first, because the run reads the persisted targets.",
    savesDirtyMix:
      "Unsaved space-mix targets will be saved first — the run uses them as segment overrides.",
    running: "Running the allocation — this can take a minute. Keep this window open.",
    mixOverAllocated:
      "Saved space-mix targets sum to {{total}}% — the run scales them down proportionally to 100%.",
    mixUnderAllocated:
      "Every segment has a space-mix target but they sum to {{total}}% — the run scales them up to fill the shelf.",
    cancel: "Cancel",
    confirm: "Run distribution",
  },

  mixConfirm: {
    title: "Space-mix targets sum to {{total}}%",
    subtitle: "The run will not apply these numbers verbatim",
    over: "Your explicit targets add up to {{total}}%, above 100%. Saving is fine — but the distribution run scales every target down by the same factor so they fit into 100%. Relative sizes survive; the absolute numbers you typed do not, and segments without a target get no share of their own.",
    underComplete:
      "Every segment has an explicit target, but together they only add up to {{total}}%. The leftover shelf has nowhere else to flow, so the run scales your targets up proportionally to fill 100%. Relative sizes survive; the absolute numbers you typed do not.",
    hint: "To have the numbers applied as typed, adjust them to sum to 100% — or leave at least one segment without a target to absorb the difference.",
    cancel: "Back to editing",
    confirm: "Save anyway",
  },

  runResult: {
    title: "Distribution complete",
    subtitle: "Finished in {{seconds}}s. Assortments are updated in place.",
    changedSkus: "SKUs changed",
    added: "Store adds",
    removed: "Store removals",
    rangedNote:
      "{{rangedRows}} ranged store/SKU rows across {{products}} SKUs and {{stores}} stores after the run.",
    warningsTitle: "{{count}} warnings",
    noWarnings: "No warnings — every constraint was satisfied.",
    close: "Close",
  },

  toasts: {
    targetsSaved: "Targets saved",
    targetsSavedMessage: "{{count}} products updated.",
    targetsSaveFailed: "Failed to save targets",
    spaceMixSavedMessage: "{{count}} segment targets updated.",
    spaceMixSaveFailed: "Failed to save space mix targets",
    nothingToSave: "No changes to save",
    runFailed: "Failed to run distribution",
    exportEmpty: "Nothing to export",
    exportEmptyMessage: "No products match the current filters.",
    imported: "CSV imported",
    importSummary:
      "{{count}} products updated, {{unknown}} unknown product codes, {{invalid}} invalid rows. Review the highlighted rows and save the targets.",
    importFailed: "Could not import the CSV",
    importNoKeyColumn: "The file needs a product_code column.",
  },
} as const;

export type DistributionPlannerTranslations = typeof distributionPlannerEn;
